import { useState } from 'react'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency'
import Loading from '../../Loading'

interface Props {
  disableDefaultValue?: boolean
  onChange: (legalEntityId?: string, currency?: string) => void
  disableMulticurrency?: boolean
}

const LegalEntitySelector = ({
  onChange,
  disableMulticurrency = false,
  disableDefaultValue = false,
}: Props) => {
  const [value, setValue] = useState<number | undefined>(undefined)
  const { company } = useCurrentSession()
  const { data, loading } = useCompanyCurrency()

  const options = useUnsafeMemo(
    () =>
      sortBy(
        company.legalEntities.map((legalEntity) => ({
          label: legalEntity.legalName ?? '',
          value: legalEntity.id.toString(),
        })),
        'label'
      ),
    [company.legalEntities]
  )

  // Can clear filter if the company only has one currency, or if multicurrency is enabled
  // The latter is decided by a combination of feature availabilites and supported currencies
  const canSeeClearFilterOption = data?.isMulticurrency || data?.multicurrencyEnabled

  const handleValueChanged = useUnsafeCallback(
    (legalEntityId?: string) => {
      if (legalEntityId) {
        setValue(Number(legalEntityId))
        onChange(
          legalEntityId,
          company.legalEntities.find((l) => l.id === Number(legalEntityId))?.currency
        )
      } else {
        setValue(undefined)
        onChange(undefined, data?.defaultCurrency)
      }
    },
    [company.legalEntities, data?.defaultCurrency] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const defaultLegalEntityId = disableDefaultValue
    ? undefined
    : data?.defaultSelectedLegalEntity?.id.toString()

  // After currentData ends loading, we know which legal entity should be
  // selected by default (if any), so we here simulate the legal entity selection
  // which both updates the value and inform the parent component about the change
  useUnsafeEffect(
    () => handleValueChanged(defaultLegalEntityId),
    [data?.defaultSelectedLegalEntity, handleValueChanged, defaultLegalEntityId]
  )

  return (
    <Loading
      loading={loading}
      skeleton={
        <Box minWidth="s140" flexDirection="row" gap="s12">
          <Skeleton height="s32" />
        </Box>
      }
    >
      <SingleChoice
        label={i18n.t('finance.workspace.legal_entity_scope.selector_label')}
        value={value?.toString()}
        onValueChange={handleValueChanged}
        options={options}
        footer={disableMulticurrency || canSeeClearFilterOption ? undefined : <></>}
      />
    </Loading>
  )
}

export default LegalEntitySelector
